import { Template, TemplateData, TemplateFormType } from 'components/ui/composed/template/TemplateContext';
import { ICS2MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { getIcs2EnsTemplateLevels } from 'views/declarations/eu/ics2/ics2-ens-template-levels';
import { Ics2EnsTemplate, Ics2EnsTemplateLevel, Ics2EnsTemplateMeta } from './ics2-ens-template';

/**
 * Translation between the template the builder edits and the shape the ICS2 ENS template API takes.
 *
 * The builder keeps each level's `defaults` as the payload itself, because that is what the
 * declaration forms write to once `useTemplatePath` has prefixed their field paths. The API wraps
 * each level in its persistence entity instead, so the payload sits one key deeper and under a
 * different name per level. Nothing outside this module needs to know that.
 */

const emptyLevel = (): TemplateData => ({ defaults: {}, meta: {} });

const meta = (level: { meta?: Record<string, Ics2EnsTemplateMeta> | null } | null | undefined) => level?.meta ?? {};

/**
 * Levels the message type does not support are left out entirely. The backend rejects the whole
 * request when it is sent one - F50 carries no master goods item, so posting `masterGoodsItem` for
 * it fails rather than being ignored.
 */
export const toIcs2EnsTemplate = (
    template: Template | undefined,
    declarationName: ICS2MessageTypes
): Ics2EnsTemplate => {
    const levels = getIcs2EnsTemplateLevels(declarationName);
    const includes = (level: TemplateFormType) => levels.includes(level);
    const result: Ics2EnsTemplate = {};

    if (includes('master') && template?.master) {
        result.declaration = {
            // `messageType` is validated against `declarationName`, so it has to agree with it.
            defaults: { messageType: declarationName, ics2EnsDeclarationRequest: template.master.defaults ?? {} },
            meta: template.master.meta ?? {},
        };
    }

    if (includes('houseConsignment') && template?.houseConsignment) {
        result.houseConsignment = {
            defaults: { houseConsignment: template.houseConsignment.defaults ?? {} },
            meta: template.houseConsignment.meta ?? {},
        };
    }

    if (includes('product') && template?.product) {
        result.masterGoodsItem = {
            defaults: { messageType: declarationName, goodsItem: template.product.defaults ?? {} },
            meta: template.product.meta ?? {},
        };
    }

    if (includes('houseGoodsItem') && template?.houseGoodsItem) {
        result.houseGoodsItem = {
            defaults: { messageType: declarationName, goodsItem: template.houseGoodsItem.defaults ?? {} },
            meta: template.houseGoodsItem.meta ?? {},
        };
    }

    return result;
};

/**
 * Unwraps the API shape back into the builder's. Every level and every field inside its `defaults`
 * is treated as possibly null: the ICS2 response type carries no non-null inclusion rule, unlike
 * the generic template response, so levels that were never filled in come back as explicit nulls
 * rather than being omitted.
 */
export const fromIcs2EnsTemplate = (template: Ics2EnsTemplate | undefined): Template => {
    const unwrap = <T>(
        level: Ics2EnsTemplateLevel<T> | null | undefined,
        defaults: unknown | null | undefined
    ): TemplateData => ({ defaults: (defaults as Record<string, any>) ?? {}, meta: meta(level) });

    const result: Template = {
        master: template?.declaration
            ? unwrap(template.declaration, template.declaration.defaults?.ics2EnsDeclarationRequest)
            : emptyLevel(),
        product: template?.masterGoodsItem
            ? unwrap(template.masterGoodsItem, template.masterGoodsItem.defaults?.goodsItem)
            : emptyLevel(),
    };

    if (template?.houseConsignment) {
        result.houseConsignment = unwrap(
            template.houseConsignment,
            template.houseConsignment.defaults?.houseConsignment
        );
    }

    if (template?.houseGoodsItem) {
        result.houseGoodsItem = unwrap(template.houseGoodsItem, template.houseGoodsItem.defaults?.goodsItem);
    }

    return result;
};
